import { Fragment } from "react";
import { BRAND_SLOT_LABEL, BRAND_SLOT_SHORT_LABEL, lockupSlots } from "@sga/shared";
import type { PortalBranding } from "./branding.js";

/**
 * The NNPC / Seplat lockup, in the order `lockupSlots` decides — the same order the form header,
 * the dashboard chrome and every email use. Nothing about the ordering lives here; that is the
 * whole point of packages/shared/src/branding/lockup.ts.
 *
 * `alt` travels with the slot rather than with the position, so swapping the marks can never leave
 * a screen reader announcing the NNPC logo as "Seplat Energy".
 */
export const Lockup = ({
  branding,
  height = "h-9",
}: {
  branding: PortalBranding;
  /** Height class. Both marks are wordmarks near 1.7:1, so height alone sizes the pair. */
  height?: string;
}): JSX.Element => {
  const slots = lockupSlots(branding.logoLayout);

  return (
    <span className="flex items-center gap-3">
      {slots.map((slot, index) => {
        const src = slot === "primary" ? branding.primaryLogoUrl : branding.secondaryLogoUrl;
        return (
          <Fragment key={slot}>
            {index > 0 && branding.logoLayout === "side_by_side" && (
              <span className="sga-lockup-rule" aria-hidden="true" />
            )}
            {src ? (
              <img
                src={src}
                alt={BRAND_SLOT_LABEL[slot]}
                className={`${height} w-auto max-w-full object-contain`}
              />
            ) : (
              // Stands in only until a superadmin uploads the real files (Design → Brand profile).
              <span
                className="font-heading text-sm font-semibold"
                style={{ color: slot === "primary" ? "var(--sga-error)" : "var(--sga-primary)" }}
              >
                {BRAND_SLOT_SHORT_LABEL[slot]}
              </span>
            )}
          </Fragment>
        );
      })}
    </span>
  );
};
